import React from "react";

/* Shared page layout wrapper for legal pages */
function LegalPageLayout({ tag, title, subtitle, children }) {
  return (<>
    <div className="bg-surface-container-low pt-32 pb-16 border-b border-outline-variant/15 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-secondary/5 to-transparent pointer-events-none" />
      <div className="max-w-3xl mx-auto px-6 relative z-10">
        <span className="inline-block px-4 py-1.5 mb-4 text-xs font-bold tracking-wider uppercase text-secondary bg-secondary-container/30 rounded-full font-label">{tag}</span>
        <h1 className="text-4xl lg:text-5xl font-headline font-extrabold text-on-primary-fixed mb-4">{title}</h1>
        <p className="text-on-surface-variant text-lg">{subtitle}</p>
      </div>
    </div>
    <div className="py-16 px-6">
      <div className="max-w-3xl mx-auto prose-bw">
        {children}
      </div>
    </div>
  </>);
}

/* Reusable prose-like elements */
const LastUpdated = ({ date }) => (
  <div className="inline-flex items-center gap-2 bg-secondary-container/20 border border-secondary/15 text-secondary text-xs font-medium px-3.5 py-1.5 rounded-full mb-10">📅 Last updated: {date}</div>
);
const H2 = ({ children, first }) => (
  <h2 className={`text-xl font-headline font-bold text-on-primary-fixed pb-3 border-b border-outline-variant/20 mb-4 ${first ? "mt-0" : "mt-12"}`}>{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="text-base font-headline font-semibold text-on-surface mt-6 mb-2">{children}</h3>
);
const P = ({ children }) => (
  <p className="text-sm text-on-surface-variant leading-relaxed mb-4">{children}</p>
);
const UL = ({ children }) => (
  <ul className="list-disc pl-5 mb-4 space-y-2">{children}</ul>
);
const LI = ({ children }) => (
  <li className="text-sm text-on-surface-variant leading-relaxed">{children}</li>
);
const A = ({ href, children, external }) => (
  <a href={href} className="text-secondary hover:underline" {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{children}</a>
);
const Strong = ({ children }) => (
  <strong className="text-on-surface font-semibold">{children}</strong>
);

/* ─── Privacy Policy ─── */
export function PrivacyPolicyPage() {
  return (
    <LegalPageLayout tag="Legal" title="Privacy Policy" subtitle="How we collect, use, protect, and share your information.">
      <LastUpdated date="April 1, 2026" />

      <H2 first>1. Introduction</H2>
      <P>Bookwello ("we", "our", "us") is a WhatsApp Business API-based booking automation platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <A href="https://bookwello.in">bookwello.in</A>, use our services, or interact with our WhatsApp-powered booking system.</P>
      <P>By using Bookwello, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access or use our services.</P>

      <H2>2. Information We Collect</H2>
      <H3>2.1 Information You Provide Directly</H3>
      <UL>
        <LI><Strong>Business Account Information:</Strong> When you register as a business client, we collect your name, email address, phone number, business name, business address, GST number (if applicable), and payment information.</LI>
        <LI><Strong>Contact Form Submissions:</Strong> Name, email address, business name, business type, and message content submitted through our website contact form.</LI>
      </UL>
      <H3>2.2 Information Collected Through WhatsApp</H3>
      <UL>
        <LI><Strong>End-User Data:</Strong> When your customers interact with your Bookwello-powered WhatsApp bot, we process their WhatsApp phone number, display name, booking details (service selected, date, time), and conversation messages.</LI>
        <LI><Strong>Message Content:</Strong> We process WhatsApp messages to facilitate the booking flow. Messages are processed in real-time and stored securely for booking record purposes.</LI>
      </UL>
      <H3>2.3 Automatically Collected Information</H3>
      <UL>
        <LI><Strong>Log Data:</Strong> IP address, browser type, operating system, referring URLs, pages visited, and timestamps.</LI>
        <LI><Strong>Cookies:</Strong> We use essential cookies to maintain your session and preferences. We do not use tracking or advertising cookies.</LI>
      </UL>

      <H2>3. How We Use Your Information</H2>
      <UL>
        <LI>To provide, operate, and maintain the Bookwello booking automation platform</LI>
        <LI>To process and manage bookings made through WhatsApp</LI>
        <LI>To send booking confirmations, reminders, and service notifications</LI>
        <LI>To process payments and generate invoices</LI>
        <LI>To communicate with you about your account, updates, support requests, and service-related notices</LI>
        <LI>To improve and personalise your experience with our platform</LI>
        <LI>To detect, prevent, and address fraud, abuse, and technical issues</LI>
        <LI>To comply with legal obligations and regulatory requirements</LI>
      </UL>

      <H2>4. WhatsApp Business API & Meta Platform</H2>
      <P>Our platform operates using the <Strong>WhatsApp Business API</Strong> provided by Meta Platforms, Inc. We process messages in compliance with Meta's WhatsApp Business Policy and Commerce Policy. Key points:</P>
      <UL>
        <LI>We act as a <Strong>Business Solution Provider (BSP)</Strong> facilitating WhatsApp messaging for our business clients.</LI>
        <LI>End-user conversations via WhatsApp are also subject to <A href="https://www.whatsapp.com/legal/privacy-policy" external>WhatsApp's Privacy Policy</A>.</LI>
        <LI>We do <Strong>not</Strong> sell, rent, or share WhatsApp conversation data with third parties for advertising or marketing purposes.</LI>
        <LI>We only send messages to users who have initiated a conversation or provided explicit opt-in consent.</LI>
      </UL>

      <H2>5. Data Sharing & Third-Party Services</H2>
      <P>We do not sell your personal data. We share information only with the following trusted service providers, strictly for operating our services:</P>
      <UL>
        <LI><Strong>Meta Platforms (WhatsApp Business API)</Strong> — message delivery and processing</LI>
        <LI><Strong>Supabase</Strong> — secure database hosting and authentication</LI>
        <LI><Strong>Razorpay</Strong> — secure payment processing (PCI-DSS compliant)</LI>
        <LI><Strong>Hostinger</Strong> — website and application hosting</LI>
      </UL>
      <P>We may also disclose your information if required by law, court order, or governmental regulation, or if necessary to protect our rights, safety, or the rights and safety of others.</P>

      <H2>6. Data Retention</H2>
      <UL>
        <LI><Strong>Account data:</Strong> Retained for as long as your account is active, plus 30 days after account closure.</LI>
        <LI><Strong>Booking records:</Strong> Retained for 12 months from the date of booking for business records and dispute resolution.</LI>
        <LI><Strong>WhatsApp conversation data:</Strong> Retained for up to 90 days for service continuity, then automatically purged.</LI>
        <LI><Strong>Payment records:</Strong> Retained as required by applicable tax and financial regulations (typically 7 years under Indian law).</LI>
      </UL>
      <P>You may request deletion of your data at any time by contacting <A href="mailto:admin@bookwello.in">admin@bookwello.in</A>.</P>

      <H2>7. Data Security</H2>
      <P>We implement industry-standard security measures to protect your data, including:</P>
      <UL>
        <LI>HTTPS/TLS encryption for all data in transit</LI>
        <LI>Encrypted database storage with restricted access controls</LI>
        <LI>Regular security audits and vulnerability assessments</LI>
        <LI>Strict access controls and employee data handling policies</LI>
      </UL>
      <P>While we strive to protect your information, no method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security.</P>

      <H2>8. Your Rights</H2>
      <P>Depending on your location, you may have the following rights regarding your personal data:</P>
      <UL>
        <LI><Strong>Right to Access:</Strong> Request a copy of the personal data we hold about you.</LI>
        <LI><Strong>Right to Correction:</Strong> Request correction of inaccurate or incomplete data.</LI>
        <LI><Strong>Right to Deletion:</Strong> Request deletion of your personal data, subject to legal retention requirements.</LI>
        <LI><Strong>Right to Restrict Processing:</Strong> Request that we limit how we use your data.</LI>
        <LI><Strong>Right to Data Portability:</Strong> Request your data in a structured, machine-readable format.</LI>
        <LI><Strong>Right to Withdraw Consent:</Strong> Where processing is based on consent, you may withdraw it at any time.</LI>
      </UL>
      <P>To exercise any of these rights, please contact us at <A href="mailto:admin@bookwello.in">admin@bookwello.in</A>. We will respond within 30 days.</P>

      <H2>9. Children's Privacy</H2>
      <P>Bookwello is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete it promptly.</P>

      <H2>10. Grievance Officer</H2>
      <P>In accordance with the Information Technology Act, 2000 and the rules made thereunder, the Grievance Officer for the purpose of this Privacy Policy is:</P>
      <P><Strong>Name:</Strong> Vijay Kumawat<br/><Strong>Email:</Strong> <A href="mailto:admin@bookwello.in">admin@bookwello.in</A><br/><Strong>Address:</Strong> Jaipur, Rajasthan, India</P>
      <P>Complaints and grievances will be addressed within 30 days of receipt.</P>

      <H2>11. Changes to This Policy</H2>
      <P>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page and updating the "Last updated" date. Continued use of our services after changes constitutes acceptance of the revised policy.</P>

      <H2>12. Contact Us</H2>
      <P>If you have any questions about this Privacy Policy or our data practices, please contact us at:</P>
      <P><Strong>Bookwello</Strong><br/>Email: <A href="mailto:admin@bookwello.in">admin@bookwello.in</A><br/>Location: Jaipur, Rajasthan, India<br/>Website: <A href="https://bookwello.in">bookwello.in</A></P>
    </LegalPageLayout>
  );
}

/* ─── Terms of Service ─── */
export function TermsOfServicePage() {
  return (
    <LegalPageLayout tag="Legal" title="Terms of Service" subtitle="Please read these terms carefully before using Bookwello.">
      <LastUpdated date="April 1, 2026" />

      <H2 first>1. Acceptance of Terms</H2>
      <P>By accessing or using Bookwello ("the Service"), operated by Bookwello ("the Company"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these Terms, you may not access or use the Service.</P>

      <H2>2. Description of Service</H2>
      <P>Bookwello provides a WhatsApp Business API-based booking automation platform for appointment-based businesses. Our services include:</P>
      <UL>
        <LI>Automated booking management via WhatsApp chatbot</LI>
        <LI>Appointment scheduling, confirmations, and reminders</LI>
        <LI>Payment collection integration via Razorpay</LI>
        <LI>Admin dashboard for booking and customer management</LI>
        <LI>Customer communication tools through WhatsApp Business API</LI>
      </UL>

      <H2>3. Eligibility</H2>
      <P>You must be at least 18 years of age to use Bookwello. By using our Service, you represent and warrant that you have the legal capacity to enter into a binding agreement and that you are not barred from using the Service under applicable laws.</P>

      <H2>4. Account Registration</H2>
      <UL>
        <LI>You must provide accurate, complete, and current information during registration.</LI>
        <LI>You are responsible for maintaining the confidentiality of your account credentials.</LI>
        <LI>You are responsible for all activities that occur under your account.</LI>
        <LI>You must notify us immediately of any unauthorised use of your account at <A href="mailto:admin@bookwello.in">admin@bookwello.in</A>.</LI>
        <LI>We reserve the right to suspend or terminate accounts that violate these Terms.</LI>
      </UL>

      <H2>5. WhatsApp Business API Compliance</H2>
      <P>As a user of Bookwello, you agree to comply with <Strong>Meta's WhatsApp Business Policy</Strong>, <Strong>WhatsApp Commerce Policy</Strong>, and all applicable guidelines. Specifically, you agree to:</P>
      <UL>
        <LI>Obtain proper opt-in consent from customers before sending them WhatsApp messages</LI>
        <LI>Use WhatsApp messaging only for legitimate business communication</LI>
        <LI>Not send unsolicited marketing, spam, or bulk promotional messages</LI>
        <LI>Not use the Service for any unlawful, deceptive, or harmful purposes</LI>
        <LI>Comply with all applicable laws regarding electronic communication and data protection</LI>
      </UL>

      <H2>6. Subscription & Payments</H2>
      <UL>
        <LI>Bookwello offers monthly subscription plans. Pricing details are listed on our website.</LI>
        <LI>All prices are in Indian Rupees (₹) and are exclusive of applicable taxes (GST).</LI>
        <LI>Payments are processed securely through <Strong>Razorpay</Strong>, a PCI-DSS compliant payment gateway.</LI>
        <LI>Subscriptions automatically renew at the end of each billing cycle unless cancelled before the renewal date.</LI>
        <LI>We reserve the right to modify pricing with at least 30 days' prior written notice.</LI>
        <LI>Failed payment attempts may result in service suspension until the outstanding balance is cleared.</LI>
      </UL>

      <H2>7. Free Trial</H2>
      <P>We may offer a free trial period for new users. At the end of the trial, you must subscribe to a paid plan to continue using the Service. We reserve the right to modify or discontinue free trials at any time.</P>

      <H2>8. Acceptable Use Policy</H2>
      <P>You agree <Strong>not</Strong> to use Bookwello to:</P>
      <UL>
        <LI>Send spam, unsolicited messages, or bulk marketing communications</LI>
        <LI>Violate any local, state, national, or international law or regulation</LI>
        <LI>Infringe upon the intellectual property rights of others</LI>
        <LI>Transmit any harmful, threatening, abusive, defamatory, or obscene content</LI>
        <LI>Attempt to gain unauthorised access to our systems or other users' accounts</LI>
        <LI>Interfere with or disrupt the Service or servers connected to the Service</LI>
        <LI>Resell, redistribute, or sublicense the Service without our written permission</LI>
      </UL>

      <H2>9. Service Availability</H2>
      <P>We strive to maintain high availability but do not guarantee uninterrupted or error-free access. We are not liable for downtime caused by scheduled maintenance, third-party service outages (including Meta/WhatsApp API, Razorpay, hosting providers), or force majeure events.</P>

      <H2>10. Intellectual Property</H2>
      <P>The Bookwello platform, including its design, code, branding, and content, is the intellectual property of Bookwello and is protected under applicable copyright and trademark laws. You retain full ownership of your business data and customer data.</P>

      <H2>11. Limitation of Liability</H2>
      <P>To the maximum extent permitted by applicable law, Bookwello shall not be liable for any indirect, incidental, special, consequential, or punitive damages. Our total aggregate liability shall not exceed the total amount paid by you in the three (3) months immediately preceding the event giving rise to the claim.</P>

      <H2>12. Indemnification</H2>
      <P>You agree to indemnify and hold harmless Bookwello, its directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Service, your violation of these Terms, or your violation of any rights of a third party.</P>

      <H2>13. Termination</H2>
      <UL>
        <LI>You may cancel your subscription at any time by contacting us or through your account settings.</LI>
        <LI>We may terminate or suspend your account if you violate these Terms.</LI>
        <LI>Upon termination, your data will be retained for 30 days before permanent deletion.</LI>
        <LI>Termination does not relieve you of any obligation to pay outstanding fees.</LI>
      </UL>

      <H2>14. Governing Law</H2>
      <P>These Terms are governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in <Strong>Jaipur, Rajasthan, India</Strong>.</P>

      <H2>15. Changes to Terms</H2>
      <P>We reserve the right to modify these Terms at any time with at least 14 days' notice before material changes take effect.</P>

      <H2>16. Contact</H2>
      <P>For questions about these Terms, contact us at:</P>
      <P><Strong>Bookwello</Strong><br/>Email: <A href="mailto:admin@bookwello.in">admin@bookwello.in</A><br/>Location: Jaipur, Rajasthan, India<br/>Website: <A href="https://bookwello.in">bookwello.in</A></P>
    </LegalPageLayout>
  );
}

/* ─── Refund Policy ─── */
export function RefundPolicyPage() {
  return (
    <LegalPageLayout tag="Legal" title="Refund & Cancellation Policy" subtitle="Our commitment to fair and transparent refund practices.">
      <LastUpdated date="April 1, 2026" />

      <H2 first>1. Overview</H2>
      <P>At Bookwello, we strive to ensure complete satisfaction with our WhatsApp booking automation platform. This Refund & Cancellation Policy outlines the terms under which you may request a refund or cancel your subscription.</P>

      <H2>2. Free Trial Period</H2>
      <P>We offer a free trial period for new users to evaluate our Service. During the trial period, no charges are applied. If you decide not to continue after the trial, simply do not subscribe — no cancellation is required and no charges will be incurred.</P>

      <H2>3. Subscription Cancellation</H2>
      <UL>
        <LI>You may cancel your subscription at any time by contacting us at <A href="mailto:admin@bookwello.in">admin@bookwello.in</A> or through your account dashboard.</LI>
        <LI>Upon cancellation, your subscription will remain active until the end of the current billing period.</LI>
        <LI>No further charges will be applied after cancellation, but payments already made for the current billing period are non-refundable (except as outlined in Section 4).</LI>
        <LI>After your subscription ends, your WhatsApp booking bot will be deactivated and your data will be retained for 30 days before permanent deletion.</LI>
      </UL>

      <H2>4. Refund Eligibility</H2>
      <H3>4.1 — 7-Day Money-Back Guarantee</H3>
      <P>We offer a <Strong>7-day money-back guarantee</Strong> for first-time subscribers. If you are not satisfied within the first 7 days of your initial paid subscription, you may request a full refund.</P>
      <UL>
        <LI>Applies only to first-time subscribers on their initial purchase</LI>
        <LI>Requests must be made within 7 calendar days of the first payment</LI>
      </UL>

      <H3>4.2 — Refunds After 7 Days</H3>
      <P>After the initial 7-day period, subscription fees are generally non-refundable. We may consider refunds on a case-by-case basis for:</P>
      <UL>
        <LI><Strong>Service Failure:</Strong> Prolonged outage exceeding 72 consecutive hours caused directly by Bookwello</LI>
        <LI><Strong>Duplicate Charges:</Strong> Accidental or erroneous duplicate billing</LI>
        <LI><Strong>Unauthorised Charges:</Strong> Charges made without your authorisation, subject to verification</LI>
      </UL>

      <H3>4.3 — Non-Refundable Situations</H3>
      <UL>
        <LI>Change of mind after the 7-day guarantee period</LI>
        <LI>Failure to use the Service during the subscription period</LI>
        <LI>Account termination due to violation of Terms of Service</LI>
        <LI>Service unavailability caused by third-party providers (Meta/WhatsApp API outages, payment gateway issues)</LI>
        <LI>Partial month usage — we do not offer pro-rated refunds</LI>
      </UL>

      <H2>5. How to Request a Refund</H2>
      <UL>
        <LI><Strong>Step 1:</Strong> Send an email to <A href="mailto:admin@bookwello.in">admin@bookwello.in</A> with subject "Refund Request".</LI>
        <LI><Strong>Step 2:</Strong> Include your registered email, business name, date of payment, and reason.</LI>
        <LI><Strong>Step 3:</Strong> Our team will review and respond within <Strong>3–5 business days</Strong>.</LI>
      </UL>

      <H2>6. Refund Processing</H2>
      <UL>
        <LI>Approved refunds will be processed within <Strong>7–10 business days</Strong>.</LI>
        <LI>Refunds are credited to the <Strong>original payment method</Strong>.</LI>
        <LI>Processed through <Strong>Razorpay</Strong>. Times may vary by bank.</LI>
        <LI>You will receive email confirmation once the refund is initiated.</LI>
      </UL>

      <H2>7. Plan Downgrades</H2>
      <P>If you wish to downgrade your plan, the change takes effect at the start of the next billing cycle. No refund or credit is issued for the difference between plan tiers during an active billing period.</P>

      <H2>8. Modifications to This Policy</H2>
      <P>We reserve the right to modify this policy at any time. Changes will be posted with an updated date. Material changes will be communicated via email at least 14 days before taking effect.</P>

      <H2>9. Contact Us</H2>
      <P>For any questions regarding refunds or cancellations:</P>
      <P><Strong>Bookwello</Strong><br/>Email: <A href="mailto:admin@bookwello.in">admin@bookwello.in</A><br/>Location: Jaipur, Rajasthan, India<br/>Website: <A href="https://bookwello.in">bookwello.in</A></P>
    </LegalPageLayout>
  );
}
